import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getMockUsers, saveMockUsers } from '../mock';
import { masterAPI } from '../services/api';
import { formatDateTime } from '../utils/currency';
import { formatPhoneNumber } from '../utils/validation';
import { Plus, Search, Shield, Edit, Trash2, Phone, Mail, Crown, AlertTriangle, ShieldAlert, Users, Building2, Save } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import SecurityActivity from '../components/SecurityActivity';

// Helper to generate unique ID - called outside render
const generateUserId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback: use timestamp + random part
  return `user_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
};

const MasterDashboard = () => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const { toast } = useToast();
  const { currentUser } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'staff'
  });
  
  const [activeTab, setActiveTab] = useState('users');
  
  // Venue limits state
  const [venueLimits, setVenueLimits] = useState([]);
  const [venueLimitsLoading, setVenueLimitsLoading] = useState(false);
  const [editingLimit, setEditingLimit] = useState(null);
  const [newLimit, setNewLimit] = useState('');
  
  // Panic mode state
  const [panicModeSettings, setPanicModeSettings] = useState([]);
  const [panicModeLoading, setPanicModeLoading] = useState(false);

  // Load users on mount
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const data = await masterAPI.getAllUsers();
        setUsers(data);
      } catch (error) {
        console.error('Failed to load users from backend, using mock data:', error);
        // Fallback to mock data
        const data = getMockUsers();
        setUsers(data);
      }
    };
    loadUsers();
  }, []);
  
  // Load venue limits when tab is activated
  useEffect(() => {
    if (activeTab === 'venues') {
      loadVenueLimits();
    }
    if (activeTab === 'security') {
      loadPanicModeSettings();
    }
  }, [activeTab]);
  
  const loadVenueLimits = async () => {
    setVenueLimitsLoading(true);
    try {
      const data = await masterAPI.getVenueLimits();
      setVenueLimits(data || []);
    } catch (error) {
      console.error('Failed to load venue limits:', error);
      toast({
        title: "Error",
        description: "Failed to load venue limits",
        variant: "destructive"
      });
    } finally {
      setVenueLimitsLoading(false);
    }
  };
  
  const loadPanicModeSettings = async () => {
    setPanicModeLoading(true);
    try {
      const data = await masterAPI.getPanicModeSettings();
      setPanicModeSettings(data || []);
    } catch (error) {
      console.error('Failed to load panic mode settings:', error);
    } finally {
      setPanicModeLoading(false);
    }
  };
  
  const handleTogglePanicMode = async (userId, currentEnabled) => {
    try {
      await masterAPI.togglePanicMode(userId, !currentEnabled);
      toast({
        title: "Success",
        description: `Panic mode ${!currentEnabled ? 'enabled' : 'disabled'} for this admin`
      });
      loadPanicModeSettings();
    } catch (error) {
      console.error('Failed to toggle panic mode:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update panic mode setting",
        variant: "destructive"
      });
    }
  };
  
  const handleUpdateVenueLimit = async (userId) => {
    if (!newLimit || isNaN(newLimit) || newLimit < 1 || newLimit > 50) {
      toast({
        title: "Invalid Limit",
        description: "Please enter a valid limit between 1 and 50",
        variant: "destructive"
      });
      return;
    }
    
    try {
      await masterAPI.updateVenueLimit(userId, parseInt(newLimit));
      toast({
        title: "Success",
        description: "Venue limit updated successfully"
      });
      setEditingLimit(null);
      setNewLimit('');
      loadVenueLimits();
    } catch (error) {
      console.error('Failed to update venue limit:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update venue limit",
        variant: "destructive"
      });
    }
  };

  // Filter users when dependencies change - use useMemo instead of useEffect+setState
  const filteredUsers = React.useMemo(() => {
    let filtered = [...users];

    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }

    if (searchQuery) {
      filtered = filtered.filter(u => 
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.phone.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [users, searchQuery, roleFilter]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingUser) {
      // Update existing user
      const updatedUsers = users.map(u => 
        u.id === editingUser.id ? { 
          ...u, 
          ...formData,
          // Don't allow changing master status
          isMaster: u.isMaster,
          canDelete: u.canDelete
        } : u
      );
      setUsers(updatedUsers);
      saveMockUsers(updatedUsers);
      toast({ title: 'Success', description: 'User updated successfully' });
    } else {
      // Create new user
      const newUser = {
        ...formData,
        id: generateUserId(),
        isMaster: false,
        canDelete: true,
        createdAt: new Date().toISOString()
      };
      const updatedUsers = [...users, newUser];
      setUsers(updatedUsers);
      saveMockUsers(updatedUsers);
      toast({ title: 'Success', description: 'User created successfully' });
    }

    resetForm();
    setDialogOpen(false);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone,
      password: user.password,
      role: user.role
    });
    setDialogOpen(true);
  };

  const handleDelete = (user) => {
    if (user.isMaster) {
      toast({ 
        title: 'Error', 
        description: 'Master account cannot be deleted',
        variant: 'destructive'
      });
      return;
    }

    if (!user.canDelete) {
      toast({ 
        title: 'Error', 
        description: 'This account is protected and cannot be deleted',
        variant: 'destructive'
      });
      return;
    }

    const updatedUsers = users.filter(u => u.id !== user.id);
    setUsers(updatedUsers);
    saveMockUsers(updatedUsers);
    toast({ title: 'Success', description: 'User deleted successfully' });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      role: 'staff'
    });
    setEditingUser(null);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      master: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 border-purple-200',
      admin: 'bg-red-100 dark:bg-red-900/30 text-red-800 border-red-200',
      staff: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 border-blue-200',
      stakeholder: 'bg-green-100 dark:bg-green-900/30 text-green-800 border-green-200',
      manager: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 border-orange-200'
    };
    return colors[role] || colors.staff;
  };

  // Check if current user is master
  if (!currentUser?.isMaster) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-foreground mb-2">Access Denied</h2>
            <p className="text-gray-600 dark:text-muted-foreground">Only Master account can access this dashboard.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Crown className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-foreground">Master Control</h1>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground mt-0.5">Control all users & security</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Users</span>
            <span className="sm:hidden">Users</span>
          </TabsTrigger>
          <TabsTrigger value="venues" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">Venue Limits</span>
            <span className="sm:hidden">Venues</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" />
            <span className="hidden sm:inline">Security</span>
            <span className="sm:hidden">Security</span>
          </TabsTrigger>
        </TabsList>

        {/* User Management Tab */}
        <TabsContent value="users" className="mt-4 space-y-4">
          {/* Add User Button */}
          <div className="flex justify-end">
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 shadow-md text-xs sm:text-sm">
                  <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
                  <DialogDescription>
                    {editingUser ? 'Update user information' : 'Create a new user account'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input id="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone * (10 digits)</Label>
                      <Input 
                        id="phone" 
                        type="tel" 
                        placeholder="XXXXX XXXXX"
                        value={formData.phone} 
                        onChange={(e) => setFormData({...formData, phone: formatPhoneNumber(e.target.value)})} 
                        pattern="[0-9]{5}\s[0-9]{5}"
                        maxLength="11"
                        required 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">Password *</Label>
                      <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="role">Role *</Label>
                      <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="staff">Staff</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700">
                      {editingUser ? 'Update' : 'Create'} User
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

      {/* Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-indigo-100">
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
              <div className="text-center sm:text-left">
                <p className="text-[10px] sm:text-xs text-purple-600">Total</p>
                <p className="text-lg sm:text-2xl font-bold text-purple-700">{users.length}</p>
              </div>
              <Shield className="w-4 h-4 sm:w-6 sm:h-6 text-purple-500 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-red-50 to-rose-100">
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
              <div className="text-center sm:text-left">
                <p className="text-[10px] sm:text-xs text-red-600">Admins</p>
                <p className="text-lg sm:text-2xl font-bold text-red-700">{users.filter(u => u.role === 'admin').length}</p>
              </div>
              <Shield className="w-4 h-4 sm:w-6 sm:h-6 text-red-500 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-cyan-100">
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
              <div className="text-center sm:text-left">
                <p className="text-[10px] sm:text-xs text-blue-600">Staff</p>
                <p className="text-lg sm:text-2xl font-bold text-blue-700">{users.filter(u => u.role === 'staff').length}</p>
              </div>
              <Shield className="w-4 h-4 sm:w-6 sm:h-6 text-blue-500 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-emerald-100">
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
              <div className="text-center sm:text-left">
                <p className="text-[10px] sm:text-xs text-green-600">Stakeholder</p>
                <p className="text-lg sm:text-2xl font-bold text-green-700">{users.filter(u => u.role === 'stakeholder').length}</p>
              </div>
              <Shield className="w-4 h-4 sm:w-6 sm:h-6 text-green-500 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-yellow-100">
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
              <div className="text-center sm:text-left">
                <p className="text-[10px] sm:text-xs text-amber-600">Master</p>
                <p className="text-lg sm:text-2xl font-bold text-amber-700">{users.filter(u => u.isMaster).length}</p>
              </div>
              <Crown className="w-4 h-4 sm:w-6 sm:h-6 text-amber-500 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
              <Input
                placeholder="Search by name, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 text-sm"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-32 h-9 text-sm">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="master">Master</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="stakeholder">Stakeholder</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <div className="grid grid-cols-1 gap-2 sm:gap-3">
        {filteredUsers.length > 0 ? (
          filteredUsers.map((user) => (
            <Card key={user.id} className={`border shadow-sm hover:shadow-md transition-shadow duration-300 ${user.isMaster ? 'ring-2 ring-purple-400' : ''}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-start space-x-3 sm:space-x-4 flex-1">
                    <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-full flex items-center justify-center font-bold text-sm sm:text-lg flex-shrink-0 ${
                      user.isMaster ? 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white' :
                      user.role === 'admin' ? 'bg-gradient-to-br from-red-500 to-pink-600 text-white' :
                      'bg-gradient-to-br from-blue-500 to-cyan-600 text-white'
                    }`}>
                      {user.isMaster ? <Crown className="w-5 h-5 sm:w-7 sm:h-7" /> : user.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                        <h3 className="text-sm sm:text-lg font-bold text-gray-900 dark:text-foreground truncate">{user.name}</h3>
                        <Badge className={`${getRoleBadgeColor(user.role)} text-[10px] sm:text-xs`}>
                          {user.role.toUpperCase()}
                        </Badge>
                        {user.isMaster && (
                          <Badge className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white border-0 text-[10px] sm:text-xs hidden sm:inline-flex">
                            PROTECTED
                          </Badge>
                        )}
                      </div>
                      <div className="space-y-0.5">
                        <div className="flex items-center text-[11px] sm:text-sm text-gray-600 dark:text-muted-foreground truncate">
                          <Mail className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-gray-400 dark:text-muted-foreground flex-shrink-0" />
                          <span className="truncate">{user.email}</span>
                        </div>
                        <div className="flex items-center text-[11px] sm:text-sm text-gray-600 dark:text-muted-foreground">
                          <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-gray-400 dark:text-muted-foreground flex-shrink-0" />
                          {user.phone}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2 sm:mt-0">
                    <Button
                      onClick={() => handleEdit(user)}
                      variant="outline"
                      size="sm"
                      className="hover:bg-purple-50 dark:bg-purple-900/20 hover:border-purple-300 hover:text-purple-700 h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                      disabled={user.isMaster && user.id !== currentUser.id}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDelete(user)}
                      variant="outline"
                      size="sm"
                      className="hover:bg-red-50 dark:bg-red-900/20 hover:border-red-300 hover:text-red-700 h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                      disabled={!user.canDelete || user.isMaster}
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      {user.isMaster ? 'Protected' : 'Delete'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="border-0 shadow-md">
            <CardContent className="p-12 text-center">
              <Shield className="w-16 h-16 text-gray-400 dark:text-muted-foreground mx-auto mb-4" />
              <p className="text-gray-600 dark:text-muted-foreground text-lg">No users found</p>
              <p className="text-gray-500 dark:text-muted-foreground text-sm mt-2">Try adjusting your filters or add a new user</p>
            </CardContent>
          </Card>
        )}
      </div>
        </TabsContent>

        {/* Venue Limits Tab */}
        <TabsContent value="venues" className="mt-4 space-y-4">
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 dark:text-foreground">
                <Building2 className="w-5 h-5 text-teal-600" />
                Admin Venue Limits
              </CardTitle>
              <p className="text-sm text-gray-500 dark:text-muted-foreground">Manage maximum venue limits for each admin. Default limit is 4 venues.</p>
            </CardHeader>
            <CardContent className="p-4">
              {venueLimitsLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                </div>
              ) : venueLimits.length === 0 ? (
                <div className="text-center p-8 text-gray-500 dark:text-muted-foreground">
                  <Building2 className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No admin accounts found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {venueLimits.map((admin) => (
                    <div 
                      key={admin.userId} 
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50 dark:bg-secondary rounded-lg gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900 dark:text-foreground truncate">{admin.name}</h4>
                          {admin.isCustomLimit && (
                            <Badge className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 text-[10px]">Custom</Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-muted-foreground truncate">{admin.email}</p>
                        {admin.venueName && (
                          <p className="text-xs text-gray-400 dark:text-muted-foreground truncate">Business: {admin.venueName}</p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="text-center">
                          <div className="text-lg font-bold text-teal-600">
                            {admin.currentVenues} / {admin.maxLimit}
                          </div>
                          <p className="text-[10px] text-gray-500 dark:text-muted-foreground">Venues Used</p>
                        </div>
                        
                        {editingLimit === admin.userId ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              max="50"
                              value={newLimit}
                              onChange={(e) => setNewLimit(e.target.value)}
                              className="w-16 h-8 text-center text-sm"
                              placeholder="Limit"
                              autoFocus
                            />
                            <Button
                              size="sm"
                              onClick={() => handleUpdateVenueLimit(admin.userId)}
                              className="h-8 bg-teal-600 hover:bg-teal-700"
                            >
                              <Save className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setEditingLimit(null); setNewLimit(''); }}
                              className="h-8"
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { 
                              setEditingLimit(admin.userId); 
                              setNewLimit(admin.maxLimit.toString()); 
                            }}
                            className="h-8 hover:bg-teal-50 dark:bg-teal-900/20 hover:border-teal-300 hover:text-teal-700"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit Limit
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="mt-4 space-y-6">
          {/* Panic Mode Settings */}
          <Card className="border shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg dark:text-foreground">
                <ShieldAlert className="w-5 h-5 text-red-600" />
                Panic Mode Access Control
              </CardTitle>
              <p className="text-sm text-gray-500 dark:text-muted-foreground">
                Enable panic mode feature for selected admins. When enabled, admins can secretly lock their accounts in emergency situations.
              </p>
            </CardHeader>
            <CardContent>
              {panicModeLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500 dark:text-muted-foreground">Loading settings...</p>
                </div>
              ) : panicModeSettings.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-muted-foreground">
                  <ShieldAlert className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No admin accounts found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {panicModeSettings.map((admin) => (
                    <div
                      key={admin.userId}
                      className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border ${
                        admin.isInPanicMode 
                          ? 'bg-red-50 dark:bg-red-900/20 border-red-300' 
                          : admin.panicModeEnabled 
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200' 
                            : 'bg-gray-50 dark:bg-secondary border-gray-200 dark:border-border'
                      }`}
                    >
                      <div className="flex-1 mb-3 sm:mb-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-foreground">{admin.name}</span>
                          {admin.isInPanicMode && (
                            <Badge className="bg-red-600 text-white text-[10px]">
                              🚨 LOCKED
                            </Badge>
                          )}
                          {admin.panicModeEnabled && !admin.isInPanicMode && (
                            <Badge className="bg-green-600 text-white text-[10px]">
                              Enabled
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-muted-foreground">{admin.email}</p>
                        {admin.venueName && (
                          <p className="text-xs text-gray-400 dark:text-muted-foreground">Venue: {admin.venueName}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={admin.panicModeEnabled}
                            onChange={() => handleTogglePanicMode(admin.userId, admin.panicModeEnabled)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:bg-card after:border-gray-300 dark:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                        </label>
                        <span className="text-sm text-gray-600 dark:text-muted-foreground min-w-[60px]">
                          {admin.panicModeEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Info box */}
              <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-800 flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  How Panic Mode Works
                </h4>
                <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                  <li><strong>Activation:</strong> Admin enters credentials + clicks &quot;Password&quot; label 5 times</li>
                  <li><strong>Effect:</strong> Locks admin account + all staff accounts, shows fake &quot;Network error&quot;</li>
                  <li><strong>Alert:</strong> You receive notification + email with IP address</li>
                  <li><strong>Recovery:</strong> Admin clicks Login button 5 times with correct credentials</li>
                </ul>
              </div>
            </CardContent>
          </Card>
          
          {/* Security Activity */}
          <SecurityActivity />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MasterDashboard;
